import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, MapPin, Save, Edit2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { getAllUbicazioni, createUbicazione, deleteUbicazione } from "@/services/localDatabase";

const Ubicazioni = () => {
  const [ubicazioni, setUbicazioni] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [formData, setFormData] = useState({ codice: "", descrizione: "" });

  useEffect(() => {
    fetchUbicazioni();
  }, []);

  const fetchUbicazioni = async () => {
    try {
      setLoading(true);
      const data = await getAllUbicazioni();
      setUbicazioni(data);
    } catch (error) {
      console.error("Error fetching ubicazioni:", error);
      toast.error("Errore nel caricamento");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!formData.codice.trim()) {
      toast.error("Il codice è obbligatorio");
      return;
    }
    try {
      await createUbicazione({ 
        codice: formData.codice, 
        descrizione: formData.descrizione,
        nome_zona: formData.codice // For compatibility
      });
      toast.success("Ubicazione creata");
      setShowDialog(false);
      setFormData({ codice: "", descrizione: "" });
      fetchUbicazioni();
    } catch (error) {
      console.error("Error saving:", error);
      toast.error("Errore nel salvataggio");
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await deleteUbicazione(deleteId);
      toast.success("Ubicazione eliminata");
      fetchUbicazioni();
    } catch (error) {
      console.error("Error deleting:", error);
      toast.error("Errore nell'eliminazione");
    } finally {
      setDeleteId(null);
    }
  };

  return (
    <div className="p-6 md:p-8 lg:p-12 fade-in" data-testid="ubicazioni-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold text-slate-900 font-barlow tracking-tight">
            Archivio Ubicazioni
          </h1>
          <p className="text-slate-500 mt-1">Zone di produzione per i documenti DDR</p>
        </div>
        <Button 
          onClick={() => setShowDialog(true)}
          className="mt-4 md:mt-0 bg-orange-500 hover:bg-orange-600 text-white font-bold uppercase tracking-wide h-11 px-8"
          data-testid="new-ubicazione-btn"
        >
          <Plus className="w-5 h-5 mr-2" />
          Nuova Ubicazione
        </Button>
      </div>

      <Card className="border-slate-100" data-testid="ubicazioni-list">
        <CardHeader className="border-b border-slate-100 bg-slate-50/50">
          <CardTitle className="font-barlow text-lg font-semibold text-slate-900 flex items-center gap-2">
            <MapPin className="w-5 h-5 text-orange-500" />
            Ubicazioni ({ubicazioni.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-slate-500">Caricamento...</div>
          ) : ubicazioni.length === 0 ? (
            <div className="p-8 text-center">
              <MapPin className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">Nessuna ubicazione salvata</p>
              <Button onClick={() => setShowDialog(true)} className="mt-4 bg-orange-500 hover:bg-orange-600">
                Aggiungi la prima ubicazione
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Codice</th>
                    <th>Descrizione</th>
                    <th>Azioni</th>
                  </tr>
                </thead>
                <tbody>
                  {ubicazioni.map((ubi) => (
                    <tr key={ubi.id} data-testid={`ubi-row-${ubi.id}`}>
                      <td className="font-mono font-medium">{ubi.codice || ubi.nome_zona}</td>
                      <td>{ubi.descrizione || "-"}</td>
                      <td>
                        <Button
                          data-testid={`delete-ubi-${ubi.id}`}
                          variant="ghost"
                          size="sm"
                          onClick={() => setDeleteId(ubi.id)}
                          className="text-slate-600 hover:text-red-500"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-[450px]">
          <DialogHeader>
            <DialogTitle className="font-barlow text-xl">Nuova Ubicazione</DialogTitle>
            <DialogDescription>Aggiungi una nuova zona di produzione</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-sm font-medium text-slate-700">Codice *</Label>
              <Input
                value={formData.codice}
                onChange={(e) => setFormData(prev => ({ ...prev, codice: e.target.value }))}
                placeholder="es. FATTIBELLO SUB 9 / 006FE188"
                className="mt-1"
                data-testid="ubi-codice"
              />
            </div>
            <div>
              <Label className="text-sm font-medium text-slate-700">Descrizione (opzionale)</Label>
              <Input
                value={formData.descrizione}
                onChange={(e) => setFormData(prev => ({ ...prev, descrizione: e.target.value }))}
                placeholder="es. Zona Fattibello"
                className="mt-1"
                data-testid="ubi-descrizione"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Annulla</Button>
            <Button onClick={handleSave} className="bg-orange-500 hover:bg-orange-600" data-testid="save-ubi-btn">
              <Save className="w-4 h-4 mr-2" />Salva
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Conferma eliminazione</AlertDialogTitle>
            <AlertDialogDescription>Sei sicuro di voler eliminare questa ubicazione?</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-500 hover:bg-red-600">Elimina</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Ubicazioni;
